import axios from 'axios';
import Multiselect from 'vue-multiselect';
import showConfirmation from '@waypoint/vue-confirmation';
import showAlert from '@waypoint/vue-alert';

import activityEscl from '@/locales/es-CL/activities.json';
import activityEsmx from '@/locales/es-MX/activities.json';
import activityEsco from '@/locales/es-CO/activities.json';
import activityEnus from '@/locales/en-US/activities.json';
import { javaObjectNotationToJson } from '../../../utils/JsonUtils';

export default {
  name: 'ActivityBuilder',
  components: {
    Multiselect,
  },
  props: {
    activityId: {
      type: String,
      default: null,
    },
    currentTrip: Object,
    currentRoute: Object,
  },
  i18n: {
    messages: {
      'es-CL': { activities: activityEscl },
      'es-MX': { activities: activityEsmx },
      'es-CO': { activities: activityEsco },
      'en-US': { activities: activityEnus },
    },
  },
  data() {
    return {
      form: null,
      documents: [],
      activityInfo: null,
      launched: false,
      launchedDocument: false,
      launchedItems: false,
      itemNames: [],
      items: [],
      documentTitles: [],
      showContactInfo: false,
      isTripNotStarted: false,
      isRouteNotEditable: true,
      tags: [],
      tagsBusy: false,
    };
  },
  async created() {
    this.launched = true;
    if (!this.currentTrip && !this.currentRoute) {
      this.$router.push({ name: 'RouteBuilder', params: { routeId: this.getStoredItem('routeBuilderRouteId') } });
    }
    this.form = this.setForm();
    if (this.currentTrip && this.currentTrip.state !== 'NOTSTARTED') {
      this.isTripNotStarted = false;
    } else {
      this.isTripNotStarted = true;
    }
    if (
      this.currentRoute &&
      (this.currentRoute.state === 'PARTIALLYASSIGNED' ||
        this.currentRoute.state === 'ASSIGNED' ||
        this.currentRoute.state === 'ONGOING')
    ) {
      this.isRouteNotEditable = true;
    } else {
      this.isRouteNotEditable = false;
    }
    await this.creationSteps();
    this.launched = false;
  },
  methods: {
    backToRoute(id) {
      this.$router.push({
        name: 'TripBuilder',
        params: {
          tripId: id,
          originId: this.currentTrip.origin.id,
          currentTrip: this.currentTrip,
          currentRoute: this.currentRoute,
        },
      });
    },
    queryItemRemoval(documentId, index) {
      const currentItem = this.items.filter((i) => i.idDocument === documentId)[index];
      if (currentItem.id) {
        showConfirmation(
          this.$t('common.modals.confirmacionTitulo'),
          this.$t('activities.activityBuilder.modals.confirmarEliminaciónItem'),
          () => {
            this.removeItem(currentItem.id);
          },
          this.$t('common.cruds.aceptar'),
          this.$t('common.cruds.cerrar'),
        );
      } else {
        const actualIndex = this.items.indexOf(currentItem);
        this.items.splice(actualIndex, 1);
      }
    },
    async removeItem(id) {
      this.items = this.items.filter((i) => i.id !== id);
      await axios.delete(this.$store.getters.urlAPI + '/itemData/' + id, {
        headers: {
          'id-client': this.$store.getters.clientId,
          Authorization: `Bearer ${this.$store.getters.accessToken}`,
        },
      });
      await this.invalidateCache();
    },
    addItems(e, index) {
      e.stopPropagation();
      if (this.documents[index].id) {
        this.items.push({
          id: null,
          idDocument: this.documents[index].id,
          idItem: null,
          quantity: '',
          unitPrice: 0,
          modified: true,
        });
        if (document.getElementById('documentBody_' + index).style.display === 'none') {
          this.$root.$emit('bv::toggle::collapse', 'documentBody_' + index);
        }
      } else {
        showConfirmation(
          this.$t('common.modals.confirmacionTitulo'),
          this.$t('activities.activityBuilder.modals.confirmarGuardadoDocumento'),
          () => {
            this.saveDocument(index);
          },
          this.$t('common.cruds.aceptar'),
          this.$t('common.cruds.cerrar'),
        );
      }
    },
    async creationSteps() {
      localStorage.removeItem('routeBuilderActivityId');
      localStorage.removeItem('routeBuilderDocumentId');
      localStorage.removeItem('routeBuilderItemId');
      if (this.activityId) {
        localStorage.setItem('routeBuilderActivityId', this.activityId);
      }
      this.getTags();
      if (this.activityId) {
        await this.getActivityInfo();
        this.form = this.setForm();
        this.showContactInfo = true;
        await this.getItemNames();
      } else {
        this.form = this.setForm();
        this.showContactInfo = true;
      }
      this.launched = false;
    },
    async getTags() {
      try {
        this.tagsBusy = true;
        const url = `${this.$store.getters.urlAPI}/tag`;
        const config = {
          headers: {
            'id-client': this.$store.getters.clientId,
            Authorization: `Bearer ${this.$store.getters.accessToken}`,
          },
        };
        const response = await axios.get(url, config);
        let filterTag = response.data.data.filter((tag) => tag.enabled === true);
        this.tagsBusy = false;
        this.tags = filterTag;
      } catch (e) {
        console.log(e);
        this.tagsBusy = false;
      }
    },
    async getActivityInfo() {
      try {
        const response = await axios.get(this.$store.getters.urlAPI + '/activity/' + this.activityId, {
          headers: {
            'id-client': this.$store.getters.clientId,
            Authorization: `Bearer ${this.$store.getters.accessToken}`,
          },
        });
        this.activityInfo = response.data.data;
        this.documents = this.activityInfo.documents ? this.activityInfo.documents.sort((a, b) => a.id - b.id) : [];
        this.documentTitles = this.documents.map((d) => ({
          id: d.id,
          number: d.number,
        }));
        this.items = [];
        this.documents.forEach((d) => {
          if (d.items && Array.isArray(d.items)) {
            this.items = [
              ...this.items,
              ...d.items.map((i) => ({
                ...i,
                idItem: {
                  id: i.idItemData,
                  name: i.name,
                },
                quantity: String(i.quantity),
                unitPrice: i.unitPrice,
                modified: false,
              })),
            ];
          }
        });
      } catch (e) {
        this.activityInfo = null;
        this.documents = [];
      }
    },
    setForm() {
      return {
        name: this.activityInfo && this.activityInfo.name ? this.activityInfo.name : '',
        type:
          this.activityInfo && this.activityInfo.type
            ? this.renderedTypes.find((t) => t.id === this.activityInfo.type)
            : this.renderedTypes[0],
        duration:
          this.activityInfo && this.activityInfo.duration ? String(Number(this.activityInfo.duration) / 60) : '',
        weight: this.activityInfo && this.activityInfo.weight ? this.activityInfo.weight : '',
        volumen: this.activityInfo && this.activityInfo.volume ? this.activityInfo.volume : '',
        tags: this.activityInfo && this.activityInfo.tags ? this.activityInfo.tags : null,
        customerName: this.activityInfo && this.activityInfo.customerName ? this.activityInfo.customerName : '',
        customerLegalNumber:
          this.activityInfo && this.activityInfo.customerLegalNumber ? this.activityInfo.customerLegalNumber : '',
        customerPhone: this.activityInfo && this.activityInfo.customerPhone ? this.activityInfo.customerPhone : '',
        customerEmail: this.activityInfo && this.activityInfo.customerEmail ? this.activityInfo.customerEmail : '',
      };
    },
    attachDocument() {
      if (this.activityId) {
        this.documents.push({
          id: null,
          number: '',
        });
        setTimeout(() => {
          window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
        }, 200);
      } else {
        showConfirmation(
          this.$t('common.modals.confirmacionTitulo'),
          this.$t('activities.activityBuilder.modals.confirmarGuardadoActividad'),
          () => {
            this.save();
          },
          this.$t('common.cruds.aceptar'),
          this.$t('common.cruds.cerrar'),
        );
      }
    },
    async getItemNames() {
      try {
        const response = await axios.get(this.$store.getters.urlAPI + '/api/itemData', {
          headers: {
            'id-client': this.$store.getters.clientId,
            Authorization: `Bearer ${this.$store.getters.accessToken}`,
          },
        });
        this.itemNames = response.data.data;
      } catch (e) {
        this.itemNames = [];
      }
    },

    async saveDocument(index) {
      this.launchedDocument = true;
      try {
        if (this.validationDocument(index)) {
          const body = {
            number: this.documents[index].number,
            status: 1,
            type: 1,
            idActivity: Number(this.activityId),
          };
          if (!this.documents[index].id) {
            const response = await axios.post(`${this.$store.getters.urlAPI}/document`, body, {
              headers: {
                'id-client': this.$store.getters.clientId,
                Authorization: `Bearer ${this.$store.getters.accessToken}`,
              },
            });
            await this.invalidateCache();
            showAlert(
              this.$t('common.modals.exitoTitulo'),
              this.$t('activities.activityBuilder.modals.guardarDocumento'),
              'success',
              () => {
                this.documents[index].id = response.data.data.id;
                this.documentTitles.push({
                  id: response.data.data.id,
                  number: response.data.data.number,
                });
                this.launchedDocument = false;
              },
              undefined,
              this.$t('common.cruds.aceptar'),
            );
          } else {
            body.id = this.documents[index].id;
            const response = await axios.patch(
              `${this.$store.getters.urlAPI}/document/${this.documents[index].id}`,
              body,
              {
                headers: {
                  'id-client': this.$store.getters.clientId,
                  Authorization: `Bearer ${this.$store.getters.accessToken}`,
                },
              },
            );

            this.documentTitles.find((d) => d.id === this.documents[index].id).number = response.data.data.number;
            await this.invalidateCache();
            showAlert(
              this.$t('common.modals.exitoTitulo'),
              this.$t('activities.activityBuilder.modals.actualizarDocumento'),
              'success',
              undefined,
              undefined,
              this.$t('common.cruds.aceptar'),
            );
            this.launchedDocument = false;
          }
        } else {
          this.launchedDocument = false;
        }
      } catch (e) {
        this.launchedDocument = false;
        if (e.response.data) {
          const respuesta = javaObjectNotationToJson(e.response.data.toString());
          const matches = respuesta.message.match(/Ha ocurido un error: Document number (.*) already exists/);
          if (matches.length > 1) {
            showAlert(
              this.$t('common.modals.errorTitulo'),
              this.$t('activities.activityBuilder.modals.errorDocumentoExistente') + matches[1],
              'error',
            );
          } else {
            showAlert(
              this.$t('common.modals.errorTitulo'),
              this.$t('activities.activityBuilder.modals.errorDocumento'),
              'error',
              undefined,
              undefined,
              this.$t('common.cruds.aceptar'),
            );
          }
        } else {
          showAlert(
            this.$t('common.modals.errorTitulo'),
            this.$t('activities.activityBuilder.modals.errorDocumento'),
            'error',
            undefined,
            undefined,
            this.$t('common.cruds.aceptar'),
          );
        }
      }
    },

    toggleDocumentBar(index) {
      this.$root.$emit('bv::toggle::collapse', 'documentBody_' + index);
    },
    async invalidateCache() {
      const invalidations = [];
      invalidations.push(
        axios.get(this.$store.getters.urlAPI + '/route/invalidate', {
          headers: {
            'id-client': this.$store.getters.clientId,
            Authorization: `Bearer ${this.$store.getters.accessToken}`,
          },
        }),
      );
      invalidations.push(
        axios.get(this.$store.getters.urlAPI + '/trip/invalidate', {
          headers: {
            'id-client': this.$store.getters.clientId,
            Authorization: `Bearer ${this.$store.getters.accessToken}`,
          },
        }),
      );
      invalidations.push(
        axios.get(this.$store.getters.urlAPI + '/activity/invalidate', {
          headers: {
            'id-client': this.$store.getters.clientId,
            Authorization: `Bearer ${this.$store.getters.accessToken}`,
          },
        }),
      );
      await Promise.all(invalidations);
    },

    async saveItems(documentId) {
      this.launchedItems = true;
      try {
        if (this.validationItems(documentId)) {
          console.log('Okey ');
          const promises = [];
          for (const item of this.items.filter((i) => i.idDocument === documentId)) {
            if (!item.id || item.modified) {
              item.modified = false;
              const body = {
                status: 1,
                idDocument: item.idDocument,
                idItemData: item.idItem.id,
                idItem: item.idItem.idItem,
                quantity: Number(item.quantity),
                unitPrice: item.unitPrice ? item.unitPrice : 0,
                code: item.idItem.code,
                name: item.idItem.name,
                description: item.idItem.description,
                extra1: item.idItem.extra1,
                extra2: item.idItem.extra2,
                extra3: item.idItem.extra3,
                extra4: item.idItem.extra4,
                extra5: item.idItem.extra5,
              };
              if (!item.id) {
                promises.push(
                  new Promise((resolve, reject) => {
                    axios
                      .post(`${this.$store.getters.urlAPI}/itemData`, body, {
                        headers: {
                          'id-client': this.$store.getters.clientId,
                          Authorization: `Bearer ${this.$store.getters.accessToken}`,
                        },
                      })
                      .then((response) => {
                        item.id = response.data.data.id;
                        resolve();
                      })
                      .catch((error) => {
                        reject(error);
                      });
                  }),
                );
              } else {
                body.id = item.id;
                promises.push(
                  axios.patch(`${this.$store.getters.urlAPI}/itemData/${item.id}`, body, {
                    headers: {
                      'id-client': this.$store.getters.clientId,
                      Authorization: `Bearer ${this.$store.getters.accessToken}`,
                    },
                  }),
                );
              }
            }
          }
          await Promise.all(promises);
          showAlert(
            this.$t('common.modals.exitoTitulo'),
            this.$t('activities.activityBuilder.modals.guardarItems'),
            'success',
            () => {
              this.launchedItems = false;
            },
            undefined,
            this.$t('common.cruds.aceptar'),
          );
          await this.invalidateCache();
        } else {
          this.launchedItems = false;
        }
      } catch (e) {
        console.log(e);
        this.launchedItems = false;
        showAlert(
          this.$t('common.modals.errorTitulo'),
          this.$t('activities.activityBuilder.modals.errorItem'),
          'error',
          undefined,
          undefined,
          this.$t('common.cruds.aceptar'),
        );
      }
    },
    validationItems(documentId) {
      for (let itemIndex = 0; itemIndex < this.items.filter((i) => i.idDocument === documentId).length; itemIndex++) {
        const result =
          this.validateItemName(documentId, itemIndex) &&
          this.validateItemQuantity(documentId, itemIndex) &&
          this.validateItemPrice(documentId, itemIndex);
        if (!result) {
          return false;
        }
      }
      return true;
    },
    validateItemName(documentId, itemIndex) {
      const currentItem = this.items.filter((i) => i.idDocument === documentId)[itemIndex];
      if (
        currentItem &&
        currentItem.idItem &&
        currentItem.idItem.id &&
        this.itemNames.find((item) => item.id === currentItem.idItem.id)
      ) {
        if (
          this.items.filter((i) => i.idDocument === documentId && i.idItem && i.idItem.id === currentItem.idItem.id)
            .length === 1
        ) {
          return true;
        }
      }

      this.showPopover('fldNombreItem_' + documentId + '_' + itemIndex);
      return false;
    },
    validateItemQuantity(documentId, itemIndex) {
      const currentItem = this.items.filter((i) => i.idDocument === documentId)[itemIndex];
      if (
        currentItem &&
        currentItem.quantity.trim().length &&
        !isNaN(currentItem.quantity) &&
        Number(currentItem.quantity) >= 0 &&
        Number(currentItem.quantity) <= 100000000
      ) {
        return true;
      } else {
        this.showPopover('fldCantidad_' + documentId + '_' + itemIndex);
        return false;
      }
    },
    validateItemPrice(documentId, itemIndex) {
      const currentItem = this.items.filter((i) => i.idDocument === documentId)[itemIndex];
      console.log(currentItem);
      if (!currentItem || (currentItem && !isNaN(currentItem.unitPrice) && Number(currentItem.unitPrice) >= 0)) {
        return true;
      } else {
        this.showPopover('fldPrice_' + documentId + '_' + itemIndex);
        return false;
      }
    },
    validationDocument(index) {
      return this.validateDocumentNumber(index);
    },

    validateDocumentNumber(index) {
      if (
        this.documents[index] &&
        this.documents[index].number &&
        this.documents[index].number.trim().length &&
        this.documents[index].number.trim().length <= 250
      ) {
        return true;
      }
      this.showPopover('fldNumeroDocumento_' + index);
      return false;
    },
    dettachDocument(e, index) {
      e.stopPropagation();
      const currentDocument = this.documents[index];
      if (currentDocument.id) {
        showConfirmation(
          this.$t('common.modals.confirmacionTitulo'),
          this.$t('activities.activityBuilder.modals.confirmarEliminaciónDocumento'),
          () => {
            this.removeDocument(currentDocument.id);
          },
          this.$t('common.cruds.aceptar'),
          this.$t('common.cruds.cerrar'),
        );
      } else {
        this.documents.splice(index, 1);
      }
    },
    secondsToTimeString(seconds) {
      let currentDate = new Date();
      currentDate.setSeconds(0);
      currentDate.setHours(0);
      currentDate.setMinutes(0);
      currentDate = new Date(currentDate.getTime() + seconds * 1000);
      return currentDate.toLocaleTimeString('en-US', { hour12: false });
    },
    timeStringToSeconds(timeString) {
      let values = timeString.split(':');
      if (values.length === 3) {
        let amount = Number(values[2]);
        amount += Number(values[1] * 60);
        amount += Number(values[0] * 60 * 60);
        return amount;
      } else {
        return 0;
      }
    },
    checkLoadUnloadTime() {
      let confirmationIsNeeded = false;
      let confirmationMessage;
      if (this.form.type.id === 'COLLECTION' && localStorage.getItem('routeSelectedUnloadTime') == 0) {
        confirmationIsNeeded = true;
        confirmationMessage = this.$t('activities.activityBuilder.modals.confirmarActividadCargaSinRutaDescarga');
      }

      if (this.form.type.id === 'DELIVERY' && localStorage.getItem('routeSelectedLoadTime') == 0) {
        confirmationIsNeeded = true;
        confirmationMessage = this.$t('activities.activityBuilder.modals.confirmarActividadDescargaSinRutaCarga');
      }

      if (confirmationIsNeeded) {
        showConfirmation(
          this.$t('common.modals.confirmacionTitulo'),
          confirmationMessage,
          () => {
            this.save();
          },
          this.$t('common.cruds.aceptar'),
          this.$t('common.cruds.cerrar'),
        );
      } else {
        this.save();
      }
    },
    async save() {
      this.launched = true;
      try {
        if (this.validation()) {
          const body = {
            idTrip: this.activityInfo
              ? Number(this.activityInfo.idTrip)
              : Number(this.getStoredItem('routeBuilderTripId')),
            name: this.form.name,
            type: this.form.type.id,
            status: 1,
            duration: Number(this.form.duration) * 60,
            weight: this.form.weight,
            volume: this.form.volumen,
            customerName: this.form.customerName !== '' ? this.form.customerName : null,
            customerLegalNumber: this.form.customerLegalNumber !== '' ? this.form.customerLegalNumber : null,
            customerPhone: this.form.customerPhone !== '' ? this.form.customerPhone : null,
            customerEmail: this.form.customerEmail !== '' ? this.form.customerEmail : null,
          };
          if (!this.activityId) {
            const response = await axios.post(`${this.$store.getters.urlAPI}/activity`, body, {
              headers: {
                'id-client': this.$store.getters.clientId,
                Authorization: `Bearer ${this.$store.getters.accessToken}`,
              },
            });
            await this.invalidateCache();
            showAlert(
              this.$t('common.modals.exitoTitulo'),
              this.$t('activities.activityBuilder.modals.guardarActividad'),
              'success',
              async () => {
                const activityId = response.data.data.id.toString();
                if (this.form.tags) {
                  await this.addTags(activityId);
                }
                this.$router.push({
                  name: 'ActivityBuilder',
                  params: { activityId: activityId, currentTrip: this.currentTrip, currentRoute: this.currentRoute },
                });
              },
              undefined,
              this.$t('common.cruds.aceptar'),
            );
          } else {
            body.id = Number(this.activityId);
            await axios.patch(`${this.$store.getters.urlAPI}/activity/${this.activityId}`, body, {
              headers: {
                'id-client': this.$store.getters.clientId,
                Authorization: `Bearer ${this.$store.getters.accessToken}`,
              },
            });
            await this.addTags(this.activityId);
            await this.invalidateCache();
            this.getActivityInfo();
            showAlert(
              this.$t('common.modals.exitoTitulo'),
              this.$t('activities.activityBuilder.modals.actualizarActividad'),
              'success',
              undefined,
              undefined,
              this.$t('common.cruds.aceptar'),
            );
            this.launched = false;
          }
        } else {
          this.launched = false;
        }
      } catch (e) {
        this.launched = false;
        showAlert(
          this.$t('common.modals.errorTitulo'),
          this.$t('activities.activityBuilder.modals.errorActividad'),
          'error',
          undefined,
          undefined,
          this.$t('common.cruds.aceptar'),
        );
      }
    },
    async addTags(activityId) {
      try {
        const body = this.form.tags ? [this.form.tags.id] : [];
        const response = await axios.patch(`${this.$store.getters.urlAPI}/activity/${activityId}/tags`, body, {
          headers: {
            'id-client': this.$store.getters.clientId,
            Authorization: `Bearer ${this.$store.getters.accessToken}`,
          },
        });
        return response.data.data;
      } catch (e) {
        console.log(e);
      }
    },
    validation() {
      return this.validateTipoActividad() && this.validateDuracion() && this.validateName() && this.notDeliveryType();
    },
    validateTipoActividad() {
      if (this.form.type) {
        if (this.form.type.id) {
          return this.renderedTypes.filter((t) => t.id == this.form.type.id).length > 0;
        }
      }
      this.showPopover('fldTipoActividad');
      return false;
    },

    validateName() {
      if (this.form.name.length > 0) {
        return true;
      }
      this.showPopover('fldNombre');
      return false;
    },

    validateDuracion() {
      if (this.form.duration && this.form.duration.trim().length) {
        if (!isNaN(this.form.duration) && Number(this.form.duration) >= 0 && Number(this.form.duration) <= 1440) {
          return true;
        }
      }
      this.showPopover('fldDuracion');
      return false;
    },
    notDeliveryType() {
      if (this.form.type && this.form.type.id !== 'DELIVERY') {
        this.form.customerName = '';
        this.form.customerLegalNumber = '';
        this.form.customerEmail = '';
        this.form.customerPhone = '';
      }
      return true;
    },
    validateTime(time) {
      if (time.trim().length) {
        const timeDecomposed = time.split(':');
        if (timeDecomposed.length === 3) {
          for (const timeUnit of timeDecomposed) {
            if (!isNaN(timeUnit) && Number(timeUnit) >= 0 && Number(timeUnit) <= 59) {
              return true;
            }
          }
        }
      } else {
        return true;
      }
      return false;
    },

    showPopover(id) {
      const wrongClass = 'wrong-input';
      this.$root.$emit('bv::show::popover', id);
      const multiselect = document.querySelector(`#${id} .multiselect`);
      if (multiselect) {
        multiselect.classList.add(wrongClass);
        return;
      }
      if (document.getElementById(id)) {
        document.getElementById(id).classList.add(wrongClass);
      }
    },
    hidePopover(id) {
      const wrongClass = 'wrong-input';
      this.$root.$emit('bv::hide::popover', id);
      const multiselect = document.querySelector(`#${id} .multiselect`);
      if (multiselect) {
        multiselect.classList.remove(wrongClass);
        return;
      }
    },
    hideAllPopovers() {
      this.$root.$emit('bv::hide::popover');
    },

    async removeDocument(id) {
      this.documents = this.documents.filter((document) => document.id !== id);
      await axios.delete(this.$store.getters.urlAPI + '/document/' + id, {
        headers: {
          'id-client': this.$store.getters.clientId,
          Authorization: `Bearer ${this.$store.getters.accessToken}`,
        },
      });
      await this.invalidateCache();
    },
    checkFormDiferences() {
      const currentForm = this.form;
      const originalForm = this.setForm();
      const keys = Object.keys(currentForm);
      for (const key of keys) {
        if (currentForm[key] !== originalForm[key]) {
          if (typeof currentForm[key] === 'object') {
            if (JSON.stringify(currentForm[key]) !== JSON.stringify(originalForm[key])) {
              return true;
            }
          } else {
            return true;
          }
        }
      }
      return false;
    },
    checkItemDifferences(documentId) {
      return this.items.filter((i) => i.idDocument === documentId && i.modified).length > 0;
    },
    checkDocumentDifferences(document) {
      const { id } = document;
      if (!id) {
        return true;
      }
      const originalDocument = this.documentTitles.find((d) => d.id === id);
      return document.number !== originalDocument.number;
    },
    getStoredItem(key) {
      return localStorage.getItem(key);
    },
  },
  computed: {
    renderedTypes() {
      return [
        {
          id: 'DELIVERY',
          name: this.$t('activities.activityBuilder.opciones.tipoActividad.DELIVERY'),
        },
        {
          id: 'COLLECTION',
          name: this.$t('activities.activityBuilder.opciones.tipoActividad.COLLECTION'),
        },
        {
          id: 'VISIT',
          name: this.$t('activities.activityBuilder.opciones.tipoActividad.VISIT'),
        },
      ];
    },
  },
  beforeRouteLeave(to, from, next) {
    if (!this.launched && this.checkFormDiferences()) {
      const answer = window.confirm(this.$t('common.cruds.pendingChanges'));
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  watch: {
    activityId() {
      this.creationSteps();
    },
    '$i18n.locale': function () {
      if (this.form.type) {
        this.form.type.name = this.renderedTypes.find((t) => t.id === this.form.type.id).name;
      }
    },
  },
};
